import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  useTheme,
  IconButton,
  Tooltip as MuiTooltip,
} from "@mui/material";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

export interface MetricDataPoint {
  date: string;
  ctr: number;
  cpc: number;
  cpm: number;
  impressions: number;
  clicks: number;
  cost: number;
  completeViews: number;
  vtr?: number;
  [key: string]: string | number | undefined;
}

export type MetricType =
  | "ctr"
  | "cpc"
  | "cpm"
  | "impressions"
  | "clicks"
  | "cost"
  | "completeViews"
  | "vtr"
  | "all";

interface MetricsTimelineChartProps {
  title: string;
  subtitle: string;
  data: MetricDataPoint[];
  isLoading?: boolean;
  hiddenMetrics?: MetricType[];
  metricLabelsOverride?: Partial<Record<MetricType | "all", string>>;
}

const MetricsTimelineChart: React.FC<MetricsTimelineChartProps> = ({
  title,
  subtitle,
  data,
  isLoading = false,
  hiddenMetrics = [],
  metricLabelsOverride,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  // Split view: each metric rendered in its own chart, avoids overlapping
  // series with very different magnitudes (e.g. impressions vs CTR)
  const [multiView, setMultiView] = useState(false);
  // Initialize selected metrics, filtering out any that are in hiddenMetrics
  const [selectedMetrics, setSelectedMetrics] = useState<MetricType[]>(
    (["ctr", "cpm"] as MetricType[]).filter(
      (metric) => !hiddenMetrics.includes(metric)
    )
  );
  const [accumulate, setAccumulate] = useState<boolean>(false);

  // Format data for the chart
  const formattedData = data.map((item, index, array) => {
    // If accumulate is true, we need to sum up all previous values
    if (accumulate && index > 0) {
      const accumulatedItem = { ...item };

      // Accumulate values for each metric
      Object.keys(item).forEach((key) => {
        const metricKey = key as keyof MetricDataPoint;
        if (key !== "date" && typeof item[metricKey] === "number") {
          // For rates (ctr, vtr), we calculate the weighted average
          if (
            key === "ctr" ||
            key === "vtr" ||
            key === "cpm" ||
            key === "cpc" ||
            key === "cpv"
          ) {
            // La tasa acumulada pondera cada día por su base (impresiones para
            // CTR/CPM/VTR, clics para CPC, vistas completas para CPV): la media
            // simple de tasas diarias daba el mismo peso a un día de 10 impresiones
            // que a uno de un millón.
            const weightKey =
              key === "cpc"
                ? "clicks"
                : key === "cpv"
                  ? "completeViews"
                  : "impressions";
            let weightedSum = 0;
            let totalWeight = 0;
            for (let i = 0; i <= index; i++) {
              const value = array[i][metricKey];
              const weight = array[i][weightKey];
              if (typeof value === "number" && typeof weight === "number") {
                weightedSum += value * weight;
                totalWeight += weight;
              }
            }
            accumulatedItem[metricKey] =
              totalWeight > 0 ? weightedSum / totalWeight : 0;
          } else {
            // For absolute values (impressions, clicks, cost), we sum them up
            let sum = 0;
            for (let i = 0; i <= index; i++) {
              const value = array[i][metricKey];
              if (typeof value === "number") {
                sum += value;
              }
            }
            accumulatedItem[metricKey] = sum;
          }
        }
      });

      return {
        ...accumulatedItem,
        date: dayjs(item.date).format("MMM DD"),
        originalDate: item.date,
      };
    }

    return {
      ...item,
      date: dayjs(item.date).format("MMM DD"),
      originalDate: item.date,
    };
  });

  // Define colors for each metric
  const metricColors: Record<Exclude<MetricType, "all">, string> = {
    ctr: theme.palette.primary.main,
    cpc: theme.palette.success.main,
    cpm: theme.palette.warning.main,
    vtr: theme.palette.secondary.main,
    impressions: "#9c27b0", // Purple
    clicks: "#2196f3", // Blue
    cost: "#ff9800", // Orange
    completeViews: "#4caf50", // Green
  };

  // Define metric labels
  const defaultMetricLabels: Record<MetricType | "all", string> = {
    all: t("common.all", { defaultValue: "All" }),
    ctr: t("home.ctr", { defaultValue: "CTR" }),
    cpc: t("home.cpc", { defaultValue: "CPC" }),
    cpm: t("home.cpm", { defaultValue: "CPM" }),
    impressions: t("home.impressions", { defaultValue: "Impressions" }),
    clicks: t("home.clicks", { defaultValue: "Clicks" }),
    cost: t("home.dashboardInvestment", { defaultValue: "Investment" }),
    completeViews: t("campaigns.completeViews", {
      defaultValue: "Complete Views",
    }),
    vtr: t("campaigns.vtr", { defaultValue: "VTR" }),
  };

  const metricLabels: Record<MetricType | "all", string> = {
    ...defaultMetricLabels,
    ...(metricLabelsOverride || {}),
  };

  // Handle metric selection change
  const handleMetricChange = (
    event: React.MouseEvent<HTMLElement>,
    metric: MetricType
  ) => {
    // Don't allow selection of hidden metrics
    if (hiddenMetrics.includes(metric)) return;
    // Prevent default to avoid form submission
    event.preventDefault();
    event.stopPropagation();

    // If All is active (accumulate is true), turn it off when selecting a metric
    if (accumulate) {
      setAccumulate(false);
    }

    // If the metric is already selected, remove it
    if (selectedMetrics.includes(metric)) {
      const newSelectedMetrics = selectedMetrics.filter((m) => m !== metric);
      setSelectedMetrics(newSelectedMetrics);
    } else {
      // Otherwise, add it to the selection
      setSelectedMetrics([...selectedMetrics, metric]);
    }
  };

  // Toggle accumulation
  const toggleAccumulation = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAccumulate(!accumulate);

    // When All is selected, clear all other metric selections
    if (!accumulate) {
      // If we're turning on accumulate (All)
      setSelectedMetrics([]);
    }
  };

  // Toggle split (multiple charts) view. Triggered by the dedicated button
  // or by clicking anywhere on the chart card.
  const toggleMultiView = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    setMultiView((prev) => !prev);
  };

  // Define tooltip props interface
  interface CustomTooltipProps {
    active?: boolean;
    payload?: Array<{
      value: number;
      dataKey: string;
      name: string;
      color: string;
    }>;
    label?: string;
  }

  // Format a metric's raw value for display (%, currency, or plain number)
  const formatMetricValue = (dataKey: string, value: number) => {
    if (dataKey === "ctr" || dataKey === "vtr") {
      return `${(value * 100).toFixed(2)}%`;
    }
    if (dataKey === "cpc" || dataKey === "cpm") {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
      }).format(value);
    }
    return value.toLocaleString();
  };

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            p: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {label}
          </Typography>
          {payload?.map((entry, index) => (
            <Typography
              key={`tooltip-${index}`}
              variant="body2"
              color={entry.color}
            >
              {metricLabels[entry.dataKey as MetricType]}:{" "}
              {formatMetricValue(entry.dataKey, entry.value)}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Paper
      onClick={() => toggleMultiView()}
      sx={{
        p: 3,
        borderRadius: 2,
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.04)",
        border: `1px solid ${theme.palette.neutral[200]}`,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        minHeight: 400,
        cursor: "pointer",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          {!multiView && (
            <>
              {/* All (accumulate) toggle */}
              <Box
                component="button"
                onClick={toggleAccumulation}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "16px",
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: accumulate
                    ? theme.palette.primary.main
                    : "transparent",
                  color: accumulate
                    ? theme.palette.primary.contrastText
                    : "inherit",
                  cursor: "pointer",
                  fontSize: "0.75rem",
                  fontFamily: "inherit",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: accumulate
                      ? theme.palette.primary.dark
                      : theme.palette.action.hover,
                  },
                }}
              >
                {t("common.all", { defaultValue: "All" })}
              </Box>

              {/* Metric selection */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {Object.entries(metricLabels).map(([key, label]) => {
                  // Skip "all" option and any hidden metrics
                  if (
                    key === "all" ||
                    hiddenMetrics.includes(key as MetricType)
                  )
                    return null;

                  const metricKey = key as MetricType;
                  const isSelected = selectedMetrics.includes(metricKey);
                  return (
                    <Box
                      key={metricKey}
                      component="button"
                      onClick={(e) => handleMetricChange(e, metricKey)}
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "16px",
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: isSelected
                          ? metricKey === "all"
                            ? theme.palette.text.primary
                            : metricColors[
                                metricKey as Exclude<MetricType, "all">
                              ]
                          : "transparent",
                        color: isSelected
                          ? metricKey === "all"
                            ? theme.palette.getContrastText(
                                theme.palette.text.primary
                              )
                            : "white"
                          : "inherit",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                        fontFamily: "inherit",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: isSelected
                            ? metricKey === "all"
                              ? theme.palette.action.selected
                              : metricColors[
                                  metricKey as Exclude<MetricType, "all">
                                ]
                            : theme.palette.action.hover,
                        },
                      }}
                    >
                      {label}
                    </Box>
                  );
                })}
              </Box>
            </>
          )}

          {/* Multiple views toggle */}
          <MuiTooltip
            title={t("home.multipleViewsTooltip", {
              defaultValue:
                "View each metric in its own chart, so different scales don't overlap",
            })}
          >
            <IconButton
              onClick={(e) => toggleMultiView(e)}
              size="small"
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "16px",
                backgroundColor: multiView
                  ? theme.palette.primary.main
                  : "transparent",
                color: multiView
                  ? theme.palette.primary.contrastText
                  : theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor: multiView
                    ? theme.palette.primary.dark
                    : theme.palette.action.hover,
                },
              }}
            >
              <ViewAgendaOutlinedIcon fontSize="small" />
            </IconButton>
          </MuiTooltip>
        </Box>
      </Box>

      {/* Chart */}
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
          }}
        >
          <Typography variant="body1">{t("common.loading")}</Typography>
        </Box>
      ) : data.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
            height: 350,
            minHeight: 350,
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 1,
            p: 2,
          }}
        >
          <Typography variant="h6" color="primary" gutterBottom align="center">
            {t("home.noMetricsTitle", {
              defaultValue: "No Metrics Available",
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            {t("home.noMetricsMessage", {
              defaultValue:
                "There are no performance metrics available for this period yet.",
            })}
          </Typography>
        </Box>
      ) : multiView ? (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              lg: "1fr 1fr 1fr",
            },
            gap: 2,
            width: "100%",
            cursor: "default",
          }}
        >
          {Object.keys(metricColors)
            .filter((metric) => !hiddenMetrics.includes(metric as MetricType))
            .map((metric) => {
              const metricKey = metric as Exclude<MetricType, "all">;
              return (
                <Box
                  key={metricKey}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: metricColors[metricKey], mb: 0.5 }}
                  >
                    {metricLabels[metricKey]}
                  </Typography>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart
                      data={formattedData}
                      margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={theme.palette.divider}
                      />
                      <XAxis
                        dataKey="date"
                        tick={{
                          fill: theme.palette.text.secondary,
                          fontSize: 10,
                        }}
                        axisLine={{ stroke: theme.palette.divider }}
                        tickLine={{ stroke: theme.palette.divider }}
                      />
                      <YAxis
                        tick={{
                          fill: theme.palette.text.secondary,
                          fontSize: 10,
                        }}
                        axisLine={{ stroke: theme.palette.divider }}
                        tickLine={{ stroke: theme.palette.divider }}
                        width={45}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          formatMetricValue(metricKey, value)
                        }
                        labelStyle={{ fontSize: 12 }}
                        contentStyle={{ fontSize: 12 }}
                      />
                      <Line
                        type="monotone"
                        dataKey={metricKey}
                        name={metricLabels[metricKey]}
                        stroke={metricColors[metricKey]}
                        strokeWidth={2}
                        dot={{ r: 2, fill: metricColors[metricKey] }}
                        activeDot={{ r: 4, fill: metricColors[metricKey] }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              );
            })}
        </Box>
      ) : (
        <Box sx={{ width: "100%", height: 350, minHeight: 350, flex: 1 }}>
          <ResponsiveContainer width="100%" height={330}>
            <LineChart
              data={formattedData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={theme.palette.divider}
              />
              <XAxis
                dataKey="date"
                tick={{ fill: theme.palette.text.secondary }}
                axisLine={{ stroke: theme.palette.divider }}
                tickLine={{ stroke: theme.palette.divider }}
              />
              <YAxis
                tick={{ fill: theme.palette.text.secondary }}
                axisLine={{ stroke: theme.palette.divider }}
                tickLine={{ stroke: theme.palette.divider }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{
                  paddingTop: 0,
                  paddingBottom: 0,
                  marginTop: -10,
                  width: "95%",
                  position: "absolute",
                  bottom: 5,
                  left: "2.5%",
                  right: "2.5%",
                  fontSize: "0.75rem",
                }}
                align="center"
                verticalAlign="bottom"
                iconSize={10}
                iconType="circle"
              />
              {accumulate || selectedMetrics.length === 0
                ? // Show all metrics if "All" is selected (accumulate is true) or no metrics are selected
                  Object.keys(metricColors)
                    .filter(
                      (metric) => !hiddenMetrics.includes(metric as MetricType)
                    )
                    .map((metric) => {
                      const metricKey = metric as Exclude<MetricType, "all">;
                      return (
                        <Line
                          key={metricKey}
                          type="monotone"
                          dataKey={metricKey}
                          name={metricLabels[metricKey]}
                          stroke={metricColors[metricKey]}
                          strokeWidth={2}
                          dot={{ r: 3, fill: metricColors[metricKey] }}
                          activeDot={{ r: 5, fill: metricColors[metricKey] }}
                        />
                      );
                    })
                : // Show only the selected metrics
                  selectedMetrics
                    .filter((metric) => !hiddenMetrics.includes(metric))
                    .map((metric) => {
                      return (
                        <Line
                          key={metric}
                          type="monotone"
                          dataKey={metric}
                          name={metricLabels[metric as MetricType]}
                          stroke={
                            metricColors[metric as Exclude<MetricType, "all">]
                          }
                          strokeWidth={2}
                          dot={{
                            r: 3,
                            fill: metricColors[
                              metric as Exclude<MetricType, "all">
                            ],
                          }}
                          activeDot={{
                            r: 5,
                            fill: metricColors[
                              metric as Exclude<MetricType, "all">
                            ],
                          }}
                        />
                      );
                    })}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

export default MetricsTimelineChart;
